import { useCallback, useEffect, useMemo, useState } from "react";
import { MensajeToastError, MensajeToastExito } from "../../componentes/FuncionesLocales";
import { useStoreHome } from "../Home/useStoreHome";
import { obtenerSesion } from "../../servicios/sesionSeguridad";
import { listarPuntosVenta, listarTiposComprobante } from "../../servicios/puntosVentaApi";
import { extraerListaPuntosVenta, normalizarPuntoVenta } from "../../servicios/puntosVentaUtils";
import { extraerLista, listarUsuarios } from "../../servicios/seguridadApi";
import TooltipAyuda from "../../ComponentesGlobales/TooltipAyuda";
import {
  extraerErrorApi,
  guardarConfiguracionArca,
  normalizarValidacionArca,
  sincronizarNumeradoresArca,
  validarConfiguracionArca,
} from "../../servicios/arcaConfiguracionApi";
import "./configuracionArca.css";

const TIPOS_WSFE = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13, 15, 49, 51, 52, 53, 54, 60, 61, 63, 201, 202, 203, 206, 207, 208, 211, 212, 213]);

const valor = (item, nombre) => item?.[nombre] ?? item?.[nombre.charAt(0).toLowerCase() + nombre.slice(1)];
const codigoArca = (item) => Number(valor(item, "sCodigo")) || 0;
const idTipo = (item) => Number(valor(item, "Id")) || 0;
const descripcionTipo = (item) => String(valor(item, "sDescripcion") || "").trim();
const datosRespuesta = (respuesta) => respuesta?.data || {};
const etiquetaUsuario = (item) => {
  const nombre = [item?.Nombre, item?.Apellido].filter(Boolean).join(" ").trim();
  const mail = String(item?.Mail || "").trim();
  return [nombre, mail].filter(Boolean).join(" — ") || `Usuario ${item?.OperadorId}`;
};

function SelectorUsuario({ usuarios, usuarioId, cargando, ocupada, onChange }) {
  return <label>
    <span>Usuario emisor * <TooltipAyuda etiqueta="Qué es el usuario emisor" contenido="Es la persona de esta empresa que quedará vinculada al certificado y emitirá las facturas electrónicas." /></span>
    <select
      required
      value={usuarioId || ""}
      disabled={ocupada || cargando}
      onChange={(event) => onChange(Number(event.target.value) || 0)}
    >
      <option value="" disabled>{cargando ? "Cargando usuarios..." : "Seleccioná un usuario"}</option>
      {usuarios.map((item) => <option key={item.OperadorId} value={item.OperadorId}>{etiquetaUsuario(item)}</option>)}
    </select>
    <small>El ID se asigna automáticamente.</small>
  </label>;
}

function Indicador({ activo, icono, titulo, detalle }) {
  return <div className={`arca-indicador ${activo ? "correcto" : "pendiente"}`}>
    <span><i className={`pi ${activo ? "pi-check" : icono}`} /></span>
    <div><strong>{titulo}<TooltipAyuda etiqueta={`Qué significa ${titulo}`} contenido={detalle} posicion="abajo" /></strong><small>{activo ? "Correcto" : "Requiere revisión"}</small></div>
  </div>;
}

export default function ConfiguracionArca() {
  const EmpresaId = useStoreHome((state) => state.EmpresaId);
  const sesion = obtenerSesion();
  const [usuarioId, setUsuarioId] = useState(sesion?.OperadorId || 0);
  const [cuit, setCuit] = useState("");
  const [archivo, setArchivo] = useState("");
  const [password, setPassword] = useState("");
  const [actualizarPassword, setActualizarPassword] = useState(true);
  const [puntoVenta, setPuntoVenta] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [cargandoUsuarios, setCargandoUsuarios] = useState(true);
  const [tipos, setTipos] = useState([]);
  const [tiposSeleccionados, setTiposSeleccionados] = useState([]);
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [cargandoPuntosVenta, setCargandoPuntosVenta] = useState(true);
  const [estado, setEstado] = useState(null);
  const [procesando, setProcesando] = useState("");

  const tiposWsfe = useMemo(() => tipos.filter((item) => TIPOS_WSFE.has(codigoArca(item))), [tipos]);
  const usuariosDisponibles = useMemo(() => {
    if (!sesion?.OperadorId || usuarios.some((item) => Number(item.OperadorId) === Number(sesion.OperadorId))) return usuarios;
    return [{
      OperadorId: sesion.OperadorId,
      Nombre: sesion.Nombre,
      Apellido: sesion.Apellido,
      Mail: sesion.Mail,
    }, ...usuarios];
  }, [sesion?.Apellido, sesion?.Mail, sesion?.Nombre, sesion?.OperadorId, usuarios]);

  const consultarEstado = useCallback(async ({ silencioso = false } = {}) => {
    if (!EmpresaId || !usuarioId) return;
    setProcesando("validar");
    const respuesta = await validarConfiguracionArca({ EmpresaId, UsuarioId: usuarioId });
    setProcesando("");
    const error = extraerErrorApi(respuesta, "No se pudo validar la configuración ARCA.");
    if (respuesta?.error || respuesta?.data?.Error || respuesta?.data?.error) {
      if (!silencioso) MensajeToastError(error);
      return;
    }
    setEstado(normalizarValidacionArca(datosRespuesta(respuesta)));
  }, [EmpresaId, usuarioId]);

  useEffect(() => {
    let activo = true;
    const cargarUsuarios = async () => {
      const respuesta = await listarUsuarios("", true);
      if (!activo) return;
      setCargandoUsuarios(false);
      if (respuesta?.error || respuesta?.data?.Error || respuesta?.data?.error) {
        MensajeToastError("No se pudo cargar la lista de usuarios activos.");
        return;
      }
      setUsuarios(extraerLista(respuesta));
    };
    cargarUsuarios();
    return () => { activo = false; };
  }, [EmpresaId]);

  useEffect(() => {
    let activo = true;
    const cargarPuntosVenta = async () => {
      if (!EmpresaId) {
        setCargandoPuntosVenta(false);
        return;
      }
      setCargandoPuntosVenta(true);
      const respuesta = await listarPuntosVenta({ EmpresaId, activo: true });
      if (!activo) return;
      setCargandoPuntosVenta(false);
      const datos = datosRespuesta(respuesta);
      if (respuesta?.error || datos?.Error || datos?.error) {
        setPuntosVenta([]);
        MensajeToastError("No se pudieron cargar los puntos de venta activos.");
        return;
      }
      const unicos = new Map();
      extraerListaPuntosVenta(datos).map(normalizarPuntoVenta).forEach((item) => {
        const numero = Number(item.sPuntoVenta);
        if (numero > 0 && !unicos.has(numero)) unicos.set(numero, item.sPuntoVenta);
      });
      const opciones = [...unicos.entries()]
        .sort(([numeroA], [numeroB]) => numeroA - numeroB)
        .map(([numero, etiqueta]) => ({ numero, etiqueta }));
      setPuntosVenta(opciones);
      setPuntoVenta((actual) => {
        if (opciones.some((item) => item.numero === Number(actual))) return actual;
        return opciones.length === 1 ? String(opciones[0].numero) : "";
      });
    };
    cargarPuntosVenta();
    return () => { activo = false; };
  }, [EmpresaId]);

  useEffect(() => {
    let activo = true;
    const cargarTipos = async () => {
      if (!EmpresaId) return;
      const respuesta = await listarTiposComprobante({ EmpresaId });
      if (!activo) return;
      const datos = datosRespuesta(respuesta);
      if (respuesta?.error || datos?.Error || datos?.error) {
        MensajeToastError(extraerErrorApi(respuesta, "No se pudieron cargar los tipos de comprobante."));
        return;
      }
      const lista = datos.Lista || datos.lista || [];
      setTipos(lista);
      const sugeridos = lista
        .filter((item) => [1, 4, 7].includes(codigoArca(item)))
        .map(idTipo)
        .filter(Boolean);
      setTiposSeleccionados(sugeridos);
    };
    cargarTipos();
    return () => { activo = false; };
  }, [EmpresaId]);

  useEffect(() => { consultarEstado({ silencioso: true }); }, [consultarEstado]);

  const guardar = async (event) => {
    event.preventDefault();
    if (!usuarioId || String(cuit).replace(/\D/g, "").length !== 11 || !archivo.trim()) {
      MensajeToastError("Completá usuario, CUIT de 11 dígitos y archivo del certificado.");
      return;
    }
    setProcesando("guardar");
    const respuesta = await guardarConfiguracionArca({
      EmpresaId,
      UsuarioId: usuarioId,
      Cuit: cuit,
      ArchivoCertificado: archivo,
      PasswordCertificado: password,
      ActualizarPassword: actualizarPassword,
    });
    setProcesando("");
    const error = extraerErrorApi(respuesta, "No se pudo guardar la configuración ARCA.");
    if (respuesta?.error || respuesta?.data?.Error || respuesta?.data?.error) {
      MensajeToastError(error);
      return;
    }
    setPassword("");
    setActualizarPassword(false);
    MensajeToastExito("Configuración del emisor guardada satisfactoriamente.");
    await consultarEstado({ silencioso: true });
  };

  const alternarTipo = (id) => setTiposSeleccionados((actual) =>
    actual.includes(id) ? actual.filter((item) => item !== id) : [...actual, id]);

  const sincronizar = async (event) => {
    event.preventDefault();
    if (!usuarioId || !Number(puntoVenta) || tiposSeleccionados.length === 0) {
      MensajeToastError("Indicá usuario, punto de venta y al menos un tipo de comprobante.");
      return;
    }
    setProcesando("sincronizar");
    const respuesta = await sincronizarNumeradoresArca({
      EmpresaId,
      UsuarioId: usuarioId,
      PuntoVenta: puntoVenta,
      ComprobantesTipoIds: tiposSeleccionados,
    });
    setProcesando("");
    const error = extraerErrorApi(respuesta, "No se pudieron sincronizar los numeradores.");
    if (respuesta?.error || respuesta?.data?.Error || respuesta?.data?.error) {
      MensajeToastError(error);
      return;
    }
    const cantidad = datosRespuesta(respuesta).Lista?.length || datosRespuesta(respuesta).lista?.length || 0;
    MensajeToastExito(`${cantidad} numerador${cantidad === 1 ? "" : "es"} sincronizado${cantidad === 1 ? "" : "s"}.`);
    await consultarEstado({ silencioso: true });
  };

  const ocupada = Boolean(procesando);
  const indicadores = [
    [estado?.configuracionGuardada, "pi-cog", "Emisor", "Confirma que el CUIT, el certificado y los datos necesarios para facturar fueron guardados."],
    [estado?.certificadoValido, "pi-id-card", "Certificado", "Comprueba que el archivo PFX/P12 está vigente, tiene una clave privada y puede identificar a la empresa ante ARCA."],
    [estado?.wsaaAutenticado, "pi-key", "Acceso a ARCA", "Comprueba que ARCA reconoce el certificado y permite iniciar una conexión segura."],
    [estado?.wsfeDisponible, "pi-cloud", "Factura electrónica", "Comprueba que el servicio de ARCA para autorizar facturas electrónicas está disponible."],
    [estado?.consumidorFinalConfigurado, "pi-user", "Consumidor final", "Comprueba que existe el cliente genérico que se usa cuando una venta no requiere identificar al comprador."],
    [estado?.numeradoresConfigurados, "pi-sort-numeric-up", "Numeradores", "Comprueba que hay una próxima numeración preparada para los comprobantes fiscales seleccionados."],
  ];

  return <main className="configuracion-arca">
    <header className="arca-encabezado">
      <div className="arca-encabezado-principal"><span className="arca-icono" aria-hidden="true"><i className="pi pi-cog" /></span><div><h1>Configuración ARCA <TooltipAyuda etiqueta="Qué es ARCA" contenido="ARCA es el organismo que autoriza las facturas electrónicas. Esta pantalla conecta la empresa con sus servicios." posicion="abajo" /></h1><p>Prepará el emisor, sincronizá numeradores y verificá la conexión fiscal.</p></div></div>
      <div className="arca-ambiente"><small>Ambiente <TooltipAyuda etiqueta="Qué significa ambiente" contenido="Indica si se trabaja con datos reales (Producción) o en un espacio de prueba (Homologación)." posicion="abajo" /></small><strong>{estado?.ambiente || "Sin verificar"}</strong></div>
    </header>

    <div className="arca-contenido vista-entrada-lateral">
      <section className={`arca-resumen ${estado?.listoParaEmitir ? "listo" : ""}`}>
        <div className="arca-resumen-titulo"><div><span className="arca-paso">Estado general <TooltipAyuda etiqueta="Qué muestra el estado general" contenido="Resume si la empresa ya puede emitir facturas electrónicas. Cada tarjeta inferior muestra un control necesario." posicion="abajo" /></span><h2>{estado?.listoParaEmitir ? "Listo para emitir" : "Configuración pendiente"}</h2><p>{estado?.listoParaEmitir ? "Todos los controles respondieron correctamente." : "Revisá los controles y completá los pasos pendientes."}</p></div><button type="button" onClick={() => consultarEstado()} disabled={ocupada}><i className={`pi ${procesando === "validar" ? "pi-spin pi-spinner" : "pi-refresh"}`} /> Verificar ahora</button></div>
        <div className="arca-indicadores">{indicadores.map(([activo, icono, titulo, detalle]) => <Indicador key={titulo} activo={Boolean(activo)} icono={icono} titulo={titulo} detalle={detalle} />)}</div>
        {estado?.problemas?.length > 0 && <div className="arca-problemas"><strong><i className="pi pi-exclamation-triangle" /> Pendientes detectados</strong><ul>{estado.problemas.map((problema) => <li key={problema}>{problema}</li>)}</ul></div>}
      </section>

      <div className="arca-pasos-grid">
        <form className="arca-panel" onSubmit={guardar}>
          <header><span className="arca-numero">1</span><div><h2>Configurar emisor <TooltipAyuda etiqueta="Qué significa configurar el emisor" contenido="Vincula un usuario y el CUIT de la empresa con el certificado digital que ARCA usa para reconocerla." posicion="abajo" /></h2><p>El certificado debe existir previamente en el servidor.</p></div></header>
          <div className="arca-formulario">
            <SelectorUsuario usuarios={usuariosDisponibles} usuarioId={usuarioId} cargando={cargandoUsuarios} ocupada={ocupada} onChange={setUsuarioId} />
            <label><span>CUIT * <TooltipAyuda etiqueta="Qué CUIT ingresar" contenido="Ingresá los 11 números del CUIT de la empresa que emitirá las facturas. Puede escribirse con o sin guiones." /></span><input required inputMode="numeric" maxLength="13" placeholder="00-00000000-0" value={cuit} onChange={(event) => setCuit(event.target.value)} /></label>
            <label className="arca-ancho"><span>Archivo PFX/P12 * <TooltipAyuda etiqueta="Qué es el archivo PFX o P12" contenido="Es el certificado digital de la empresa. Escribí el nombre exacto del archivo que un administrador ya cargó en el servidor, por ejemplo certificado-empresa.pfx." /></span><input required placeholder="certificado-empresa.pfx" value={archivo} onChange={(event) => setArchivo(event.target.value)} /><small>Informá solamente el nombre, no una ruta local.</small></label>
            <label className="arca-ancho"><span>Contraseña del certificado <TooltipAyuda etiqueta="Qué contraseña ingresar" contenido="Es la contraseña definida al crear el archivo PFX/P12. No es la clave fiscal ni la contraseña con la que ingresás al sistema." /></span><input type="password" autoComplete="new-password" value={password} disabled={!actualizarPassword} onChange={(event) => setPassword(event.target.value)} /></label>
            <label className="arca-check arca-ancho"><input type="checkbox" checked={actualizarPassword} onChange={(event) => setActualizarPassword(event.target.checked)} /><span>Actualizar la contraseña almacenada <TooltipAyuda etiqueta="Cuándo actualizar la contraseña" contenido="Marcá esta opción solo al guardar la contraseña por primera vez o cuando haya cambiado. Si la desmarcás, se conserva la que ya está guardada." /></span></label>
          </div>
          <footer><button className="arca-primario" type="submit" disabled={ocupada}><i className={`pi ${procesando === "guardar" ? "pi-spin pi-spinner" : "pi-save"}`} /> Guardar emisor</button></footer>
        </form>

        <form className="arca-panel" onSubmit={sincronizar}>
          <header><span className="arca-numero">2</span><div><h2>Sincronizar numeradores <TooltipAyuda etiqueta="Qué significa sincronizar numeradores" contenido="Consulta en ARCA el último número usado y prepara el siguiente para evitar facturas repetidas o fuera de orden." posicion="abajo" /></h2><p>Se consulta el último autorizado y nunca se reduce el número local.</p></div></header>
          <div className="arca-formulario">
            <SelectorUsuario usuarios={usuariosDisponibles} usuarioId={usuarioId} cargando={cargandoUsuarios} ocupada={ocupada} onChange={setUsuarioId} />
            <label><span>Punto de venta * <TooltipAyuda etiqueta="Qué punto de venta elegir" contenido="Elegí el punto de venta electrónico que usarás para emitir. La lista muestra únicamente los puntos activos configurados para esta empresa." /></span><select required value={puntoVenta} disabled={ocupada || cargandoPuntosVenta || puntosVenta.length === 0} onChange={(event) => setPuntoVenta(event.target.value)}><option value="" disabled>{cargandoPuntosVenta ? "Cargando puntos de venta..." : puntosVenta.length === 0 ? "No hay puntos de venta activos" : "Seleccioná un punto de venta"}</option>{puntosVenta.map((item) => <option key={item.numero} value={item.numero}>{item.etiqueta}</option>)}</select>{!cargandoPuntosVenta && puntosVenta.length === 0 && <small>Primero creá o activá un punto desde la pantalla Puntos de venta.</small>}</label>
            <fieldset className="arca-tipos arca-ancho"><legend>Comprobantes electrónicos * <TooltipAyuda etiqueta="Qué comprobantes seleccionar" contenido="Elegí los tipos de factura o nota que se emitirán desde este punto de venta. El número en verde es el código usado por ARCA." posicion="abajo" /></legend>{tiposWsfe.length === 0 ? <p>No hay tipos de comprobante electrónico disponibles en el catálogo.</p> : tiposWsfe.map((tipo) => { const id = idTipo(tipo); return <label key={id} className={tiposSeleccionados.includes(id) ? "seleccionado" : ""}><input type="checkbox" checked={tiposSeleccionados.includes(id)} onChange={() => alternarTipo(id)} /><span><strong>{codigoArca(tipo)}</strong>{descripcionTipo(tipo)}</span></label>; })}</fieldset>
          </div>
          <footer><button className="arca-primario" type="submit" disabled={ocupada || tiposWsfe.length === 0}><i className={`pi ${procesando === "sincronizar" ? "pi-spin pi-spinner" : "pi-sync"}`} /> Sincronizar con ARCA</button></footer>
        </form>
      </div>

      {estado?.numeradores?.length > 0 && <section className="arca-panel arca-numeradores"><header><span className="arca-numero">3</span><div><h2>Numeradores configurados <TooltipAyuda etiqueta="Qué muestra esta lista" contenido="Muestra la numeración que el sistema usará en la próxima factura de cada punto de venta y tipo de comprobante." posicion="abajo" /></h2><p>Estado local asociado al usuario emisor.</p></div></header><div className="arca-tabla"><table><thead><tr><th>Punto de venta <TooltipAyuda etiqueta="Qué indica punto de venta" contenido="Número del punto de venta electrónico registrado en ARCA." posicion="abajo" /></th><th>Tipo ARCA <TooltipAyuda etiqueta="Qué indica tipo ARCA" contenido="Código que ARCA asigna a cada clase de comprobante, como factura A, B o C." posicion="abajo" /></th><th>Próximo número <TooltipAyuda etiqueta="Qué indica próximo número" contenido="Número que se asignará al siguiente comprobante de este tipo. Se calcula a partir del último autorizado por ARCA." posicion="abajo" /></th></tr></thead><tbody>{estado.numeradores.map((item) => <tr key={`${valor(item, "PuntoVenta")}-${valor(item, "ComprobanteTipoId")}`}><td>{valor(item, "PuntoVenta")}</td><td>{valor(item, "TipoComprobanteArca")}</td><td>{valor(item, "ProximoNumero")}</td></tr>)}</tbody></table></div></section>}
    </div>
  </main>;
}
